<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <div class="card my-5">
          <div class="card-header bgnscblue">
            <h4 class="mb-0 text-uppercase txtwhite vue">Your Costs</h4>
          </div>
          <div class="card-body">
            <p class="lead text-center">
              This report combines the latest research on employment costs with
              data from the National Survey on Drug Use and Health (NSDUH) to
              calculate how much mental distress and illness in your workforce
              costs your company annually, and the savings associated with
              support and treatment.
            </p>
            <span id="TotalCost">
                <h1 class="h1span white bgnscgreen mb-4 mx-3">
                    TOTAL COST: <span>{{ toCurrency(model.calculations.OverallCosts) }}</span>
                </h1>
            </span>
            <div class="row px-3">
              <div class="col-xs-12 col-sm-4 mx-auto text-center">
                <div class="cardgray fixh">
                  <div class="hovereffect">
                    <a data-target="#losttime"
                       data-original-title="How is total lost time calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/substance/rx_calc_lost_time.jpg"
                           class="img-fluid"
                           alt="How is total lost time calculated?"
                           title="How is total lost time calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#losttime"
                           class="btn btn-light txtdark btn-md"
                           data-placement="top"
                           data-original-title="How is total lost time calculated?"
                           @click="showModal('losttime')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 vue">Lost Time</h2>
                  <div class="whiteinner">
                    <h6 class="mb-2 text-uppercase">Cost:</h6>
                    <span id="DaysOffWorkCost">
                        <h3 class="txtgreen mb-1 vue">
                            <span>
                                {{
                                    // model.calculations.AbsenteeismCost.toLocaleString('en-US', {style: 'currency', currency: 'USD'})
                                    toCurrency(model.calculations.AbsenteeismCost)
                                }}
                            </span>
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-xs-12 col-sm-4 mx-auto text-center">
                <div class="cardgray fixh">
                  <div class="hovereffect">
                    <a data-target="#turnover"
                       data-original-title="How is total lost time calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/substance/rx_calc_turnover.jpg"
                           class="img-fluid"
                           alt="Job Turnover and Re-Training"
                           title="Job Turnover and Re-Training"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#turnover"
                           class="btn btn-light txtdark btn-md"
                           data-placement="top"
                           data-original-title="Job Turnover Information"
                           @click="showModal('turnover')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 vue">
                    Job Turnover &amp;<br/>
                    Re-training
                  </h2>
                  <div class="whiteinner">
                    <h6 class="mb-2 text-uppercase">Cost:</h6>
                    <span id="TurnoverCost">
                        <h3 class="txtgreen mb-1 vue">
                            <span>{{ toCurrency(model.calculations.TurnoverCost) }}</span>
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-xs-12 col-sm-4 mx-auto text-center">
                <div class="cardgray fixh">
                  <div class="hovereffect">
                    <a data-target="#healthcare"
                       data-original-title="How is total health care cost calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/substance/rx_calc_healthcare.jpg"
                           class="img-fluid"
                           alt="How is total health care cost calculated?"
                           title="How is total health care cost calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#healthcare"
                           class="btn btn-light txtdark btn-md"
                           data-original-title="How is total health care cost calculated?"
                           @click="showModal('healthcare')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 vue">Health Care</h2>
                  <div class="whiteinner">
                    <h6 class="mb-2 text-uppercase">Cost:</h6>
                    <span id="HealthcareCost">
                        <h3 class="txtgreen mb-1 vue">
                            <span>{{ toCurrency(model.calculations.HealthcareCost) }}</span>
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col text-center">
                <button type="button"
                        class="mt-3 mb-4 btn btn-light txtdark btn-lgpull-right"
                        @click="toggleCost()"
                >
                  Expand Cost Detail
                  <i class="fa fa-chevron-down" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <transition name="slide">
              <div v-if="showExpandDetails" class="container">
                <div class="row">
                  <div class="col">
                    <p class="mt-3 mb-4 text-center">
                      Below is the breakout of factors that contribute to employer
                      costs in lost time, job turnover and healthcare. Mental health 
                      is associated with other problems that can impact
                      employees' productivity and safety, though these problems
                      are more difficult to monetize.
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-4 whitebg">
                    <div class="card">
                      <table class="table table-striped table-hover mb-0">
                        <thead class="font-weight-bold text-uppercase text-center">
                        <tr>
                          <td colspan="2" class="border-0">Annual Stats</td>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td>Missed Days Due to Illness:</td>
                          <td>
                            <span id="AbsenteeismSick">
                              {{ model.calculations.DaysSick.toLocaleString() }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td>Days of Work Skipped:</td>
                          <td>
                            <span id="AbsenteeismSkip">
                              {{ model.calculations.DaysSkipped.toLocaleString() }}</span>
                          </td>
                        </tr>
                        <tr class="hidden">
                          <td>
                            Number of Employees Who Will Cause a Safety Incident:
                          </td>
                          <td>
                            <span id="DetailEmployeeErrors">
                              {{ model.calculations.EmployeesWhoCauseSafetyIncident.toLocaleString() }}</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div class="col-md-4 whitebg">
                    <div class="card">
                      <table class="table table-striped table-hover mb-0">
                        <thead class="font-weight-bold text-uppercase text-center">
                        <tr>
                          <td colspan="2" class="border-0">Excess Annual Turnover</td>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td>Total Turnover:</td>
                          <td>
                            <span id="Turnover">{{ model.calculations.Turnover.toLocaleString() }}</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div class="col-md-4 whitebg">
                    <div class="card">
                        <table class="table table-striped table-hover mb-0">
                            <thead class="font-weight-bold text-uppercase text-center">
                                <tr>
                                    <td colspan="2" class="border-0">Excess Annual Health Care Use</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Days in hospital:</td>
                                    <td>
                                        <span id="DetailDaysInHospital">
                                            {{ model.calculations.HospitalDays.toLocaleString() }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Emergency room visits:</td>
                                    <td>
                                        <span id="DetailERVisits">
                                            {{ model.calculations.ERVisits.toLocaleString() }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td>Outpatient visits:</td>
                                    <td>
                                        <span id="DetailOutpatientVisits">
                                            {{ model.calculations.PrimaryCareVisits.toLocaleString() }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                  </div>
                </div>
              </div>
            </transition>

          </div>
        </div>

        <div class="card my-5">
            <div class="card-header bgnscblue">
                <h4 class="mb-0 text-uppercase txtwhite vue">
                    Mental Distress Impacts the Workplace
                </h4>
            </div>
            <div class="card-body">
                <p class="lead">
                    Mental distress and illness impacts the bottom line in several ways.
                </p>
                <div class="row">
                    <div class="col-md-4">
                        <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/infographic-health-care.png" class="img-fluid" alt="Health Care" title="Health Care" />
                    </div>
                    <div class="col-md-4">
                        <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/infographic-days-work-missed.png" class="img-fluid" alt="Days Missed" title="Days Missed" />
                    </div>
                    <div class="col-md-4">
                        <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/infographic-turnover.png" class="img-fluid" alt="Turnover" title="Turnover" />
                    </div>
                </div>
            </div>
        </div>

        <div class="card my-5">
          <div class="card-header bgnscblue">
            <h4 class="mb-0 text-uppercase txtwhite vue">Who's Affected?</h4>
          </div>
          <div class="card-body">
            <p class="lead">
              Your employees and their family members can struggle with mental
              distress and illness. These graphics illustrate how many people in
              your organization may be affected, broken out by group. <a style="color: #006bab; cursor: pointer;" data-target="#affected" data-placement="top" data-original-title="How is the number of employees and dependents of employees with severe psychological distress calculated?" @click="showModal('affected')"><u>Learn More</u></a>
            </p>
            <div class="row px-3">
              <div class="col-xs-12 col-sm-6 mx-auto text-center">
                <div class="cardgray">
                  <div class="hovereffect">
                    <a data-target="#employees"
                       data-original-title="How is the number of employees estimated to have a substance use disorder calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/substance/rx_calc_employees.jpg"
                           class="img-fluid"
                           alt="How is the number of employees estimated to have a substance use disorder calculated?"
                           title="How is the number of employees estimated to have a substance use disorder calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#employees"
                           class="btn btn-light txtdark btn-lg"
                           data-placement="top"
                           data-original-title="How is the number of employees estimated to have a substance use disorder calculated?"
                           @click="showModal('employees')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-4 vue">Employees</h2>
                  <span id="EmployeeCount">
                    <div class="whiteinner">
                        <h3 class="mt-2 mb-2 vue">
                            {{ model.calculations.NumEmployeesWithDistress }}
                        </h3>
                    </div>
                </span>
                </div>
              </div>
              <div class="col-xs-12 col-sm-6 mx-auto text-center">
                <div class="cardgray">
                  <div class="hovereffect">
                    <a data-target="#dependents"
                       data-original-title="How is number of dependents & family calculated?"
                    >
                      <img
                              src="https://nscmediacdn.azureedge.net/nsc/calculators/substance/rx_calc_dependents_family.jpg"
                              class="img-fluid"
                              alt="How is number of dependents & family calculated?"
                              title="How is number of dependents & family calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#dependents"
                           class="btn btn-light txtdark btn-lg"
                           data-placement="top"
                           data-original-title="How is number of dependents & family calculated?"
                           @click="showModal('dependents')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-4 vue">Dependents &amp; Family</h2>
                  <span id="DependentsCount">
                    <div class="whiteinner">
                        <h3 class="mt-2 mb-2 vue">
                            {{ model.calculations.NumDependentsWithDistress }}
                        </h3>
                    </div>
                </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card my-5">
          <div class="card-header bgnscblue">
            <h4 class="mb-0 text-uppercase txtwhite vue">Mental Health Conditions</h4>
          </div>
          <div class="card-body">
            <p class="lead">
              It is important to note that individuals may be experiencing more than one type of mental distress.
            </p>
            <div class="row px-3">
              <div class="col-xs-12 col-sm-3 mx-auto text-center">
                <div class="cardgray sizeFix">
                  <div class="hovereffect">
                    <a data-target="#depression"
                       data-original-title="How is number of employees and family calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/mhcc-depression.jpg"
                           class="img-fluid"
                           alt="How is number of employees and family calculated?"
                           title="How is number of employees and family calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#depression"
                           class="btn btn-light txtdark btn-sm"
                           data-placement="top"
                           data-original-title="How is number of employees and family calculated?"
                           @click="showModal('depression')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 sizer vue">Depression</h2>
                  <div class="whiteinner">
                    <h5 class="mt-2 mb-2">Employees:</h5>
                    <span id="EmployeeDepression">
                        <h3 class="mb-4 vue">
                            <!--{{ EmployeeDepression }}-->
                          {{model.calculations.EmployedWithDepression}}
                        </h3>
                    </span>
                    <h5 class="mb-2">Family:</h5>
                    <span id="DependentsDepression">
                        <h3 class="mb-2 vue">
                            <!--{{ DependentsDepression }}-->
                          {{model.calculations.GeneralWithDepression}}
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-xs-12 col-sm-3 mx-auto text-center">
                <div class="cardgray sizeFix">
                  <div class="hovereffect">
                    <a data-target="#anxiety"
                       data-original-title="How is number of employees and family calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/mhcc-anxiety.jpg"
                           class="img-fluid"
                           alt="How is number of employees and family calculated?"
                           title="How is number of employees and family calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#anxiety"
                           class="btn btn-light txtdark btn-sm"
                           data-placement="top"
                           data-original-title="How is number of employees and family calculated?"
                           @click="showModal('anxiety')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 sizer vue">Anxiety</h2>
                  <div class="whiteinner">
                    <h5 class="mt-2 mb-2">Employees:</h5>
                    <span id="EmployeeAnxiety">
                        <h3 class="mb-4 vue">
                            <!--{{ EmployeeAnxiety }}-->
                          {{model.calculations.EmployedWithAnxiety}}
                        </h3>
                    </span>
                    <h5 class="mb-2">Family:</h5>
                    <span id="DependentsAnxiety">
                        <h3 class="mb-2 vue">
                            <!--{{ DependentsAnxiety }}-->
                          {{model.calculations.GeneralWithAnxiety}}
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-xs-12 col-sm-3 mx-auto text-center">
                <div class="cardgray sizeFix">
                  <div class="hovereffect">
                    <a data-target="#suicide"
                       data-original-title="How is number of employees and family calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/mhcc-suicide.jpg"
                           class="img-fluid"
                           alt="How is number of employees and family calculated?"
                           title="How is number of employees and family calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#suicide"
                           class="btn btn-light txtdark btn-sm"
                           data-placement="top"
                           data-original-title="How is number of employees and family calculated?"
                           @click="showModal('suicide')"
                        >
                          Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 sizer vue">Suicidal Ideation</h2>
                  <div class="whiteinner">
                    <h5 class="mt-2 mb-2">Employees:</h5>
                    <span id="EmployeeSuicide">
                        <h3 class="mb-4 vue">
                            <!--{{ EmployeeSuicide }}-->
                          {{model.calculations.EmployedSuicidalIdeation}}
                        </h3>
                    </span>
                    <h5 class="mb-2">Family:</h5>
                    <span id="DependentsSuicide">
                        <h3 class="mb-2 vue">
                            <!--{{ DependentsSuicide }}-->
                          {{model.calculations.GeneralWithSuicidalIdeation}}
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-xs-12 col-sm-3 mx-auto text-center">
                <div class="cardgray sizeFix">
                  <div class="hovereffect">
                    <a data-target="#substance"
                       data-original-title="How is number of employees and family calculated?"
                    >
                      <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/mhcc-substance.jpg"
                           class="img-fluid"
                           alt="How is number of employees and family calculated?"
                           title="How is number of employees and family calculated?"
                      />
                    </a>
                    <div class="overlay">
                      <h2>
                        <a data-target="#substance"
                           class="btn btn-light txtdark btn-sm"
                           data-placement="top"
                           data-original-title="How is number of employees and family calculated?"
                           @click="showModal('substance')"
                        >Learn More
                        </a>
                      </h2>
                    </div>
                  </div>
                  <h2 class="mt-4 mb-2 sizer vue">
                      Substance Use Comorbidity
                  </h2>
                  <div class="whiteinner">
                    <h5 class="mt-2 mb-2">Employees:</h5>
                    <span id="EmployeeOther">
                        <h3 class="mb-4 vue">
                            <!--{{ EmployeeOther }}-->
                          {{model.calculations.EmployeesWithComorbidSUD}}
                        </h3>
                    </span>
                    <h5 class="mb-2">Family:</h5>
                    <span id="DependentsOther">
                        <h3 class="mb-2 vue">
                            <!--{{ DependentsOther }}-->
                          {{model.calculations.GeneralWithComorbidSUD}}
                        </h3>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card my-5">
          <div class="card-header bgnscblue">
            <h4 class="mb-0 text-uppercase txtwhite vue">
              Save by Addressing Mental Health
            </h4>
          </div>
          <div class="card-body">
            <p class="lead">
              Mental distress and illness can be an expensive problem for
              employers. However, providing resources to assist workers with
              their mental health can be a very good investment.
            </p>
            <div class="row justify-content-center">
                <div class="col-md-4">
                    <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/infographic-invested.png" class="img-fluid" alt="Invested" title="Invested" />
                </div>
                <div class="col-md-4">
                    <img src="https://nscmediacdn.azureedge.net/nsc/calculators/mental-health/infographic-treatment.png" class="img-fluid" alt="Treatment" title="Treatment" />
                </div>
            </div>
            <div class="row">
              <div class="col text-center">
                <a href="https://www.nsc.org/workplacementalhealth" target="_blank"
                   class="mt-5 mb-4 btn btn-lg bgnscblue txtwhite"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";
import ClickOutside from 'vue-click-outside';

export default {
    props: ['model'],
    data() {
        return {
            loc: {},
            state: store.state,
            showExpandDetails: false
        }
    },
    created() {
        console.log("model: ", this.model);
        if (this.model === null || typeof (this.model) === 'undefined') {
            this.loc = {};
            this.loc.state = {};
            this.loc.calculations = {
                NumEmployeesWithDistress: -1,
                NumDependentsWithDistress: -1,
                HealthcareCost: -1,
                AbsenteeismCost: -1,
                TurnoverCost: -1,
                OverallCosts: -1
            };
        }
    },
    methods: {
        showModal(modal) {
            store.showModal(modal);
        },
        hideModal() {
            store.hideModal();
        },
        toggleCost() {
            console.log("showing details")
            this.showExpandDetails = !this.showExpandDetails;
        },
        toCurrency(amount) {
            return amount.toLocaleString('en-US', {style: 'currency', currency: 'USD'}).split('.')[0];
        }
    },
    computed: {
        comploc() {
            return this.loc || {
                state: {},
                calculations: {
                    NumEmployeesWithDistress: -1,
                    NumDependentsWithDistress: -1,
                    HealthcareCost: -1,
                    AbsenteeismCost: -1,
                    TurnoverCost: -1,
                    OverallCosts: -1
                }
            }
        }
    },
    directives: {
        ClickOutside
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: ease-in;
    -webkit-transition-timing-function: ease-in;
    -o-transition-timing-function: ease-in;
    transition-timing-function: ease-in;
}

.slide-leave-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to, .slide-leave {
    max-height: 100px;
    overflow: hidden;
}

.slide-enter, .slide-leave-to {
    overflow: hidden;
    max-height: 0;
}

</style>
